import { UserInputError } from "./errors-CEDWDXew.js";
import { ParamAssignedTo, ParamIgnoreCount, ParamIgnoreDurationMinutes, ParamIgnoreUserCount, ParamIgnoreUserWindowMinutes, ParamIgnoreWindowMinutes, ParamIssueIgnoreMode, ParamIssueShortId, ParamIssueStatus, ParamIssueUrl, ParamOrganizationSlug, ParamReason, ParamRegionUrl } from "./schema-DKEjeVqP.js";
import { logIssue } from "./logging-B7X3yL0T.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { parseIssueParams } from "./issue-helpers-0Dnme3Ym.js";
import { assertIssueWithinProjectConstraint } from "./issue-D65xI0F7.js";
import { formatAssignedTo } from "./formatting-Dh3LlFmw.js";
import { setTag } from "@sentry/core";

//#region src/tools/catalog/update-issue.ts
function pluralize(value, noun) {
	return `${value} ${noun}${value === 1 ? "" : "s"}`;
}
function hasExplicitIgnoreChange(params) {
	return params.ignoreMode !== void 0 || params.ignoreDurationMinutes !== void 0 || params.ignoreCount !== void 0 || params.ignoreWindowMinutes !== void 0 || params.ignoreUserCount !== void 0 || params.ignoreUserWindowMinutes !== void 0;
}
function inferIgnoreMode(params) {
	if (params.ignoreMode) return params.ignoreMode;
	if (params.ignoreDurationMinutes !== void 0) return "forDuration";
	if (params.ignoreCount !== void 0 || params.ignoreWindowMinutes !== void 0) return "untilOccurrenceCount";
	if (params.ignoreUserCount !== void 0 || params.ignoreUserWindowMinutes !== void 0) return "untilUserCount";
	return "untilEscalating";
}
function getIssueStatusDetails(issue) {
	const statusDetails = issue.statusDetails;
	if (statusDetails && typeof statusDetails === "object" && !Array.isArray(statusDetails)) return statusDetails;
	return {};
}
function getStatusDetailNumber(statusDetails, key) {
	const value = statusDetails[key];
	if (typeof value === "number") return value;
	return void 0;
}
function getStatusDetailString(statusDetails, key) {
	const value = statusDetails[key];
	if (typeof value === "string" && value.length > 0) return value;
	return void 0;
}
function formatIgnoreUntil(ignoreUntil) {
	const date = new Date(ignoreUntil);
	if (Number.isNaN(date.getTime())) return ignoreUntil;
	return `${date.toISOString().slice(0, 19).replace("T", " ")} UTC`;
}
function getIssueStatusDisplay(issue) {
	const statusDetails = getIssueStatusDetails(issue);
	if (issue.status === "resolved" && statusDetails.inNextRelease === true) return "resolvedInNextRelease";
	return issue.status;
}
function getIgnoreStateFromUpdate(ignoreUpdate) {
	if (!ignoreUpdate) return null;
	switch (ignoreUpdate.substatus) {
		case "archived_until_escalating": return {
			family: "untilEscalating",
			behavior: ignoreUpdate.behavior,
			message: ignoreUpdate.message
		};
		case "archived_forever": return {
			family: "forever",
			behavior: ignoreUpdate.behavior,
			message: ignoreUpdate.message
		};
		case "archived_until_condition_met": return {
			family: "condition",
			behavior: ignoreUpdate.behavior,
			message: ignoreUpdate.message
		};
	}
}
function getIgnoreState(issue, fallbackIgnoreUpdate) {
	if (issue.status !== "ignored") return null;
	const statusDetails = getIssueStatusDetails(issue);
	const ignoreUntil = getStatusDetailString(statusDetails, "ignoreUntil");
	const ignoreDuration = getStatusDetailNumber(statusDetails, "ignoreDuration");
	const ignoreCount = getStatusDetailNumber(statusDetails, "ignoreCount");
	const ignoreWindow = getStatusDetailNumber(statusDetails, "ignoreWindow");
	const ignoreUserCount = getStatusDetailNumber(statusDetails, "ignoreUserCount");
	const ignoreUserWindow = getStatusDetailNumber(statusDetails, "ignoreUserWindow");
	if (issue.substatus === "archived_until_escalating" || statusDetails.ignoreUntilEscalating === true) return {
		family: "untilEscalating",
		behavior: "Until escalating",
		message: "The issue is now ignored until it escalates"
	};
	if (issue.substatus === "archived_forever") return {
		family: "forever",
		behavior: "Forever",
		message: "The issue is now ignored indefinitely"
	};
	if (ignoreUntil) {
		const formattedIgnoreUntil = formatIgnoreUntil(ignoreUntil);
		return {
			family: "condition",
			behavior: `Until ${formattedIgnoreUntil}`,
			message: `The issue is now ignored until ${formattedIgnoreUntil}`
		};
	}
	if (ignoreDuration !== void 0) return {
		family: "condition",
		behavior: `For ${pluralize(ignoreDuration, "minute")}`,
		message: `The issue is now ignored for ${pluralize(ignoreDuration, "minute")}`
	};
	if (ignoreCount !== void 0 && ignoreWindow !== void 0) return {
		family: "condition",
		behavior: `Until it occurs ${pluralize(ignoreCount, "time")} in ${pluralize(ignoreWindow, "minute")}`,
		message: `The issue is now ignored until it occurs ${pluralize(ignoreCount, "time")} in ${pluralize(ignoreWindow, "minute")}`
	};
	if (ignoreCount !== void 0) return {
		family: "condition",
		behavior: `Until it occurs ${pluralize(ignoreCount, "more time")}`,
		message: `The issue is now ignored until it occurs ${pluralize(ignoreCount, "more time")}`
	};
	if (ignoreUserCount !== void 0 && ignoreUserWindow !== void 0) return {
		family: "condition",
		behavior: `Until it affects ${pluralize(ignoreUserCount, "user")} in ${pluralize(ignoreUserWindow, "minute")}`,
		message: `The issue is now ignored until it affects ${pluralize(ignoreUserCount, "user")} in ${pluralize(ignoreUserWindow, "minute")}`
	};
	if (ignoreUserCount !== void 0) return {
		family: "condition",
		behavior: `Until it affects ${pluralize(ignoreUserCount, "more user")}`,
		message: `The issue is now ignored until it affects ${pluralize(ignoreUserCount, "more user")}`
	};
	if (issue.substatus === "archived_until_condition_met") return getIgnoreStateFromUpdate(fallbackIgnoreUpdate) ?? {
		family: "condition",
		behavior: "Until the ignore condition is met",
		message: "The issue is now ignored until the configured condition is met"
	};
	return {
		family: "forever",
		behavior: "Forever",
		message: "The issue is now ignored indefinitely"
	};
}
function buildNoChangesOutput(params) {
	const { issue, organizationSlug, ignoreState, issueUrl } = params;
	let output = `# Issue ${issue.shortId} Already Matches Requested State in **${organizationSlug}**\n\n`;
	output += `**Issue**: ${issue.title}\n`;
	output += `**URL**: ${issueUrl}\n\n`;
	output += "## Changes Made\n\n";
	output += "No changes were needed.\n";
	output += "\n## Current Status\n\n";
	output += `**Status**: ${getIssueStatusDisplay(issue)}\n`;
	if (ignoreState) output += `**Ignore Behavior**: ${ignoreState.behavior}\n`;
	output += `**Assigned To**: ${formatAssignedTo(issue.assignedTo ?? null)}\n`;
	output += "\n## Response Notes\n\n";
	output += "- The issue already matched the requested state.\n";
	output += `- Full issue details: \`get_sentry_resource(resourceType="issue", organizationSlug="${organizationSlug}", resourceId="${issue.shortId}")\`\n`;
	return output;
}
function isAssigneeAlreadySet(issue, requestedAssignee, currentUserId) {
	if (!requestedAssignee || !issue.assignedTo) return false;
	if (typeof issue.assignedTo === "string") return issue.assignedTo === requestedAssignee;
	if (requestedAssignee === "me") return issue.assignedTo.type === "user" && currentUserId !== void 0 && String(issue.assignedTo.id) === currentUserId;
	if (requestedAssignee.startsWith("user:")) return issue.assignedTo.type === "user" && String(issue.assignedTo.id) === requestedAssignee.slice(5);
	if (requestedAssignee.startsWith("team:")) {
		const requestedTeam = requestedAssignee.slice(5);
		return issue.assignedTo.type === "team" && (String(issue.assignedTo.id) === requestedTeam || issue.assignedTo.name === requestedTeam);
	}
	return false;
}
function getIgnoreFamily(ignoreUpdate) {
	switch (ignoreUpdate?.substatus) {
		case "archived_until_escalating": return "untilEscalating";
		case "archived_forever": return "forever";
		case "archived_until_condition_met": return "condition";
		default: return void 0;
	}
}
function isIgnoreBehaviorAlreadySet(currentIgnoreState, ignoreUpdate) {
	const requestedIgnoreState = getIgnoreStateFromUpdate(ignoreUpdate);
	return currentIgnoreState !== null && requestedIgnoreState !== null && currentIgnoreState.family === requestedIgnoreState.family && currentIgnoreState.behavior === requestedIgnoreState.behavior;
}
function buildIgnoreUpdate(params, options) {
	const hasIgnoreOptions = hasExplicitIgnoreChange(params);
	if (!hasIgnoreOptions) {
		if (params.status === "ignored" && options.applyDefaultIgnoredMode) return {
			substatus: "archived_until_escalating",
			behavior: "Until escalating",
			message: "The issue is now ignored until it escalates"
		};
		return void 0;
	}
	if (params.status !== "ignored") throw new UserInputError("Ignore options can only be used when `status` is `ignored`");
	if (params.ignoreWindowMinutes !== void 0 && params.ignoreCount === void 0) throw new UserInputError("`ignoreWindowMinutes` requires `ignoreCount`");
	if (params.ignoreUserWindowMinutes !== void 0 && params.ignoreUserCount === void 0) throw new UserInputError("`ignoreUserWindowMinutes` requires `ignoreUserCount`");
	const usesDuration = params.ignoreDurationMinutes !== void 0;
	const usesOccurrence = params.ignoreCount !== void 0 || params.ignoreWindowMinutes !== void 0;
	const usesUserCount = params.ignoreUserCount !== void 0 || params.ignoreUserWindowMinutes !== void 0;
	const configuredIgnoreFamilies = [
		usesDuration,
		usesOccurrence,
		usesUserCount
	].filter(Boolean).length;
	if (configuredIgnoreFamilies > 1) throw new UserInputError("Choose only one ignore condition: duration, occurrence count, or user count");
	const ignoreMode = inferIgnoreMode(params);
	switch (ignoreMode) {
		case "untilEscalating":
			if (configuredIgnoreFamilies > 0) throw new UserInputError("`ignoreMode='untilEscalating'` cannot be combined with ignore duration, occurrence, or user-count conditions");
			return {
				substatus: "archived_until_escalating",
				behavior: "Until escalating",
				message: "The issue is now ignored until it escalates"
			};
		case "forever":
			if (configuredIgnoreFamilies > 0) throw new UserInputError("`ignoreMode='forever'` cannot be combined with ignore duration, occurrence, or user-count conditions");
			return {
				substatus: "archived_forever",
				behavior: "Forever",
				message: "The issue is now ignored indefinitely"
			};
		case "forDuration":
			if (params.ignoreDurationMinutes === void 0) throw new UserInputError("`ignoreMode='forDuration'` requires `ignoreDurationMinutes`");
			if (usesOccurrence || usesUserCount) throw new UserInputError("`ignoreMode='forDuration'` cannot be combined with ignore occurrence or user-count conditions");
			return {
				substatus: "archived_until_condition_met",
				ignoreDuration: params.ignoreDurationMinutes,
				behavior: `For ${pluralize(params.ignoreDurationMinutes, "minute")}`,
				message: `The issue is now ignored for ${pluralize(params.ignoreDurationMinutes, "minute")}`
			};
		case "untilOccurrenceCount":
			if (params.ignoreCount === void 0) throw new UserInputError("`ignoreMode='untilOccurrenceCount'` requires `ignoreCount`");
			if (usesDuration || usesUserCount) throw new UserInputError("`ignoreMode='untilOccurrenceCount'` cannot be combined with ignore duration or user-count conditions");
			return {
				substatus: "archived_until_condition_met",
				ignoreCount: params.ignoreCount,
				ignoreWindow: params.ignoreWindowMinutes,
				behavior: params.ignoreWindowMinutes === void 0 ? `Until it occurs ${pluralize(params.ignoreCount, "more time")}` : `Until it occurs ${pluralize(params.ignoreCount, "time")} in ${pluralize(params.ignoreWindowMinutes, "minute")}`,
				message: params.ignoreWindowMinutes === void 0 ? `The issue is now ignored until it occurs ${pluralize(params.ignoreCount, "more time")}` : `The issue is now ignored until it occurs ${pluralize(params.ignoreCount, "time")} in ${pluralize(params.ignoreWindowMinutes, "minute")}`
			};
		case "untilUserCount":
			if (params.ignoreUserCount === void 0) throw new UserInputError("`ignoreMode='untilUserCount'` requires `ignoreUserCount`");
			if (usesDuration || usesOccurrence) throw new UserInputError("`ignoreMode='untilUserCount'` cannot be combined with ignore duration or occurrence conditions");
			return {
				substatus: "archived_until_condition_met",
				ignoreUserCount: params.ignoreUserCount,
				ignoreUserWindow: params.ignoreUserWindowMinutes,
				behavior: params.ignoreUserWindowMinutes === void 0 ? `Until it affects ${pluralize(params.ignoreUserCount, "more user")}` : `Until it affects ${pluralize(params.ignoreUserCount, "user")} in ${pluralize(params.ignoreUserWindowMinutes, "minute")}`,
				message: params.ignoreUserWindowMinutes === void 0 ? `The issue is now ignored until it affects ${pluralize(params.ignoreUserCount, "more user")}` : `The issue is now ignored until it affects ${pluralize(params.ignoreUserCount, "user")} in ${pluralize(params.ignoreUserWindowMinutes, "minute")}`
			};
	}
	throw new UserInputError("Unsupported ignore mode");
}
async function tryPostReasonComment(apiService, organizationSlug, issueId, reason) {
	if (!reason) return {
		posted: false,
		skipped: true
	};
	try {
		await apiService.createIssueComment({
			organizationSlug,
			issueId,
			text: reason
		});
		return { posted: true };
	} catch (error) {
		logIssue(error);
		return {
			posted: false,
			error: error instanceof Error ? error.message : String(error)
		};
	}
}
function formatReasonCommentLine(reason, result) {
	if (!reason || "skipped" in result && result.skipped) return "";
	if (result.posted) return `- **Comment posted**: "${reason}"\n`;
	if ("error" in result) return `- **Comment not posted**: ${result.error}\n`;
	return "";
}
var update_issue_default = defineTool({
	name: "update_issue",
	skills: ["triage"],
	requiredScopes: ["event:write"],
	description: [
		"Update a Sentry issue's status or assignment.",
		"",
		"Use this to resolve, reopen, assign, or ignore an issue.",
		"",
		"<examples>",
		"```",
		"update_issue(organizationSlug='my-org', issueId='PROJECT-123', status='resolved')",
		"update_issue(organizationSlug='my-org', issueId='PROJECT-123', assignedTo='user:123456')",
		"update_issue(organizationSlug='my-org', issueId='PROJECT-123', status='ignored')",
		"update_issue(organizationSlug='my-org', issueId='PROJECT-123', status='ignored', ignoreMode='forever')",
		"update_issue(organizationSlug='my-org', issueId='PROJECT-123', status='ignored', ignoreMode='untilOccurrenceCount', ignoreCount=100, ignoreWindowMinutes=60)",
		"update_issue(organizationSlug='my-org', issueId='PROJECT-123', status='ignored', reason='Ignoring because this is expected noise from the staging deploy')",
		"```",
		"</examples>",
		"",
		"<hints>",
		"- Provide `issueUrl` or `organizationSlug` + `issueId`.",
		"- At least one of `status` or `assignedTo` is required.",
		"- `assignedTo` format: `user:ID` or `team:ID_OR_SLUG`.",
		"- Use `execute_sentry_tool(name='whoami', arguments={})` to find your user ID for self-assignment.",
		"- Status values: `resolved`, `resolvedInNextRelease`, `unresolved`, `ignored`.",
		"- `status='ignored'` defaults to `ignoreMode='untilEscalating'`.",
		"- Ignore modes: `untilEscalating`, `forever`, `forDuration`, `untilOccurrenceCount`, `untilUserCount`.",
		"- Matching ignore inputs are `ignoreDurationMinutes`, `ignoreCount` + optional `ignoreWindowMinutes`, or `ignoreUserCount` + optional `ignoreUserWindowMinutes`.",
		"- To switch an already ignored issue between `untilEscalating`, `forever`, and condition-based ignore modes, first set `status='unresolved'`, then ignore it again with the new rule.",
		"- `reason` is optional. When provided, it will be posted as a comment on the issue's activity feed explaining why the action was taken.",
		"</hints>"
	].join("\n"),
	inputSchema: {
		organizationSlug: ParamOrganizationSlug.optional(),
		regionUrl: ParamRegionUrl.nullable().default(null),
		issueId: ParamIssueShortId.optional(),
		issueUrl: ParamIssueUrl.optional(),
		status: ParamIssueStatus.optional(),
		assignedTo: ParamAssignedTo.optional(),
		ignoreMode: ParamIssueIgnoreMode.optional(),
		ignoreDurationMinutes: ParamIgnoreDurationMinutes.optional(),
		ignoreCount: ParamIgnoreCount.optional(),
		ignoreWindowMinutes: ParamIgnoreWindowMinutes.optional(),
		ignoreUserCount: ParamIgnoreUserCount.optional(),
		ignoreUserWindowMinutes: ParamIgnoreUserWindowMinutes.optional(),
		reason: ParamReason.optional()
	},
	annotations: {
		readOnlyHint: false,
		destructiveHint: true,
		idempotentHint: false,
		openWorldHint: true
	},
	async handler(params, context) {
		const apiService = apiServiceFromContext(context, { regionUrl: params.regionUrl ?? void 0 });
		if (!params.issueUrl && !params.issueId) throw new UserInputError("Either `issueId` or `issueUrl` must be provided");
		if (!params.issueUrl && !params.organizationSlug) throw new UserInputError("`organizationSlug` is required when providing `issueId`");
		if (!params.status && !params.assignedTo) throw new UserInputError("At least one of `status` or `assignedTo` must be provided to update the issue");
		const { organizationSlug: orgSlug, issueId: parsedIssueId } = parseIssueParams({
			organizationSlug: params.organizationSlug,
			issueId: params.issueId,
			issueUrl: params.issueUrl
		});
		setTag("organization.slug", orgSlug);
		const currentIssue = await apiService.getIssue({
			organizationSlug: orgSlug,
			issueId: parsedIssueId
		});
		assertIssueWithinProjectConstraint({
			issue: currentIssue,
			projectSlug: context.constraints.projectSlug
		});
		const currentIgnoreState = getIgnoreState(currentIssue);
		const assignmentAlreadySet = isAssigneeAlreadySet(currentIssue, params.assignedTo, context.userId);
		const ignoreUpdate = buildIgnoreUpdate(params, { applyDefaultIgnoredMode: params.status === "ignored" && currentIssue.status !== "ignored" });
		const requestedIgnoreFamily = getIgnoreFamily(ignoreUpdate);
		const ignoreBehaviorAlreadySet = isIgnoreBehaviorAlreadySet(currentIgnoreState, ignoreUpdate);
		const statusAlreadySet = params.status !== void 0 && params.status !== "ignored" && getIssueStatusDisplay(currentIssue) === params.status;
		let updateStatus = statusAlreadySet ? void 0 : params.status;
		const updateAssignedTo = assignmentAlreadySet ? void 0 : params.assignedTo;
		let updateIgnore = ignoreUpdate;
		if (currentIssue.status === "ignored" && params.status === "ignored") {
			if (!hasExplicitIgnoreChange(params)) {
				updateStatus = void 0;
				updateIgnore = void 0;
			} else if (ignoreBehaviorAlreadySet) {
				updateStatus = void 0;
				updateIgnore = void 0;
			} else if (currentIgnoreState && requestedIgnoreFamily && currentIgnoreState.family !== requestedIgnoreFamily) throw new UserInputError("Changing ignore behavior on an already ignored issue between `untilEscalating`, `forever`, and condition-based modes is not supported. First set `status` to `unresolved`, then ignore it again with the new rule.");
		}
		const requestedIssueUrl = apiService.getIssueUrl(orgSlug, currentIssue.shortId);
		if (!updateStatus && !updateAssignedTo && !updateIgnore) {
			const commentResult$1 = await tryPostReasonComment(apiService, orgSlug, parsedIssueId, params.reason);
			return buildNoChangesOutput({
				issue: currentIssue,
				organizationSlug: orgSlug,
				ignoreState: currentIgnoreState,
				issueUrl: requestedIssueUrl
			}) + formatReasonCommentLine(params.reason, commentResult$1);
		}
		const updatedIssue = await apiService.updateIssue({
			organizationSlug: orgSlug,
			issueId: parsedIssueId,
			status: updateStatus,
			assignedTo: updateAssignedTo,
			substatus: updateIgnore?.substatus,
			ignoreDuration: updateIgnore?.ignoreDuration,
			ignoreCount: updateIgnore?.ignoreCount,
			ignoreWindow: updateIgnore?.ignoreWindow,
			ignoreUserCount: updateIgnore?.ignoreUserCount,
			ignoreUserWindow: updateIgnore?.ignoreUserWindow
		});
		const commentResult = await tryPostReasonComment(apiService, orgSlug, parsedIssueId, params.reason);
		const updatedIgnoreState = getIgnoreState(updatedIssue, ignoreUpdate);
		const currentStatusDisplay = getIssueStatusDisplay(currentIssue);
		const updatedStatusDisplay = getIssueStatusDisplay(updatedIssue);
		const statusChanged = currentStatusDisplay !== updatedStatusDisplay;
		const assignmentChanged = formatAssignedTo(currentIssue.assignedTo ?? null) !== formatAssignedTo(updatedIssue.assignedTo ?? null);
		const ignoreBehaviorChanged = currentIgnoreState?.behavior !== updatedIgnoreState?.behavior;
		let output = `# Issue ${updatedIssue.shortId} Updated in **${orgSlug}**\n\n`;
		output += `**Issue**: ${updatedIssue.title}\n`;
		output += `**URL**: ${apiService.getIssueUrl(orgSlug, updatedIssue.shortId)}\n\n`;
		output += "## Changes Made\n\n";
		if (statusChanged) output += `**Status**: ${currentStatusDisplay} → **${updatedStatusDisplay}**\n`;
		if (updatedIgnoreState && (statusChanged || ignoreBehaviorChanged) && (ignoreBehaviorChanged || params.status === "ignored")) if (currentIgnoreState && ignoreBehaviorChanged) output += `**Ignore Behavior**: ${currentIgnoreState.behavior} → **${updatedIgnoreState.behavior}**\n`;
		else output += `**Ignore Behavior**: **${updatedIgnoreState.behavior}**\n`;
		if (updateAssignedTo && assignmentChanged) {
			const oldAssignee = formatAssignedTo(currentIssue.assignedTo ?? null);
			const newAssignee = params.assignedTo === "me" ? "You" : formatAssignedTo(updatedIssue.assignedTo ?? null);
			output += `**Assigned To**: ${oldAssignee} → **${newAssignee}**\n`;
		}
		output += "\n## Current Status\n\n";
		output += `**Status**: ${updatedStatusDisplay}\n`;
		if (updatedIgnoreState) output += `**Ignore Behavior**: ${updatedIgnoreState.behavior}\n`;
		const currentAssignee = formatAssignedTo(updatedIssue.assignedTo ?? null);
		output += `**Assigned To**: ${currentAssignee}\n`;
		output += "\n## Response Notes\n\n";
		output += `- The issue has been updated in Sentry.\n`;
		output += `- Full issue details: \`get_sentry_resource(resourceType="issue", organizationSlug="${orgSlug}", resourceId="${updatedIssue.shortId}")\`\n`;
		if (statusChanged && updatedStatusDisplay === "resolved") output += `- The issue is now marked as resolved and will no longer generate alerts\n`;
		else if (statusChanged && updatedStatusDisplay === "resolvedInNextRelease") output += `- The issue is now marked as resolved in the upcoming release\n`;
		else if (updatedIgnoreState && params.status === "ignored" && (statusChanged || ignoreBehaviorChanged)) output += `- ${updatedIgnoreState.message}\n`;
		output += formatReasonCommentLine(params.reason, commentResult);
		return output;
	}
});

//#endregion
export { update_issue_default };
//# sourceMappingURL=update-issue-q9a5YNJb.js.map